// ============================================================
// AssignmentDashboard.jsx
// ============================================================
// Junior-dev Notes:
// - Layout: Persistent Drawer for Desktop, Temporary Drawer for Mobile.
// - State: Tracks mobile sidebar toggle and "hover" effects on cards.
// - Logic: Centralized route mapping for Assignments and Quizzes.
// - Branding: prodevconsult.com | Helsinki Engineering Standards
// ============================================================

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Box,
    AppBar,
    Toolbar,
    List,
    Typography,
    Divider,
    IconButton,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Container,
    Grid,
    Paper,
    Avatar,
    Chip,
    Stack,
    useTheme,
    useMediaQuery, Button, Drawer,
} from "@mui/material";

/* Icons */
import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import CodeIcon from "@mui/icons-material/Code";
import QuizIcon from "@mui/icons-material/Quiz";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import TerminalIcon from "@mui/icons-material/Terminal";
import BarChartIcon from "@mui/icons-material/BarChart";
import { DiJava, DiJavascript1, } from "react-icons/di";
import { SiTypescript } from "react-icons/si";
import {PiFileCpp} from "react-icons/pi";
import {styled} from "@mui/system";
import SettingsInputComponentIcon from "@mui/icons-material/SettingsInputComponent";

/* ── Brand tokens ────────────────────────────────────────── */
const GOLD = "#E5C95F";
const NAVY_DEEP = "#100720";
const NAVY_LIGHT = "#1A112E";
const TEXT_MUTED = "rgba(255, 255, 255, 0.7)";

const DRAWER_WIDTH = 260;

/* ── Styled Components ───────────────────────────────────── */

const StatCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    backgroundColor: "rgba(255, 255, 255, 0.03)",
    border: `1px solid rgba(229, 201, 95, 0.2)`,
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    transition: "0.3s",
    "&:hover": {
        transform: "translateY(-5px)",
        borderColor: GOLD,
    },
}));

const CourseCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    backgroundColor: NAVY_LIGHT,
    borderRadius: "16px",
    border: "1px solid rgba(255,255,255,0.05)",
    transition: "0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    overflow: "hidden",
    cursor: "pointer",
    "&:hover": {
        borderColor: GOLD,
        boxShadow: `0 10px 30px rgba(0,0,0,0.5)`,
        "& .icon-bg": { transform: "scale(1.2)", opacity: 0.1 }
    }
}));

/* ── Main Component ──────────────────────────────────────── */

export default function AssignmentDashboard() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [mobileOpen, setMobileOpen] = useState(false);
    const navigate = useNavigate();

    const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

    // Data for Analytics
    const stats = [
        { label: "Total Assignments", count: "24", icon: <AssignmentTurnedInIcon sx={{ color: GOLD }} /> },
        { label: "Quizzes Completed", count: "12", icon: <QuizIcon sx={{ color: GOLD }} /> },
        { label: "Avg. Accuracy", count: "88%", icon: <BarChartIcon sx={{ color: GOLD }} /> },
    ];

    // Course Navigation Logic
    const courses = [
        { name: "Java Master", path: "/java-assignments", quiz: "/java-quiz", icon: <DiJava size={40} />, color: "#f89820", totalQuizzes: 5 },
        { name: "JavaScript ES6", path: "/javascript-assignment", quiz: "/javascript-quiz", icon: <DiJavascript1 size={40} />, color: "#f7df1e", totalQuizzes: 8 },
        { name: "C++ Systems", path: "/cpp-assignment", quiz: "/cpp-quiz", icon: <PiFileCpp size={40} />, color: "#00599c", totalQuizzes: 4 },
        { name: "TypeScript Architecture", path: "/typescript-assignment", quiz: "/typescript-quiz", icon: <SiTypescript size={32} />, color: "#3178c6", totalQuizzes: 6 },
        // Add this to your courses array in AssignmentDashboard.jsx
        {
            name: "System Design & Hardware",
            path: "/system-design-assignment",
            quiz: "/system-design-quiz",
            icon: <SettingsInputComponentIcon size={32} />,
            color: "#00ED64",
            totalQuizzes: 4
        },
    ];

    /* ── Sidebar Content ── */
    const drawerContent = (
        <Box sx={{ height: "100%", bgcolor: NAVY_DEEP, color: "white" }}>
            <Toolbar sx={{ justifyContent: "center", py: 4 }}>
                <Typography variant="h5" fontWeight={900}>
                    PRO<span style={{ color: GOLD }}>DEV</span>
                </Typography>
            </Toolbar>
            <Divider sx={{ bgcolor: "rgba(255,255,255,0.05)" }} />
            <List sx={{ px: 2, mt: 2 }}>
                {[
                    { text: "Dashboard", icon: <DashboardIcon /> },
                    { text: "Course Hub", icon: <CodeIcon />, path: "/course-hub" },
                    { text: "Live Compiler", icon: <TerminalIcon />, path: "/compiler" },
                    { text: "My Assignments", icon: <DashboardIcon /> },
                    { text: "My Java Ass. Solutions", icon: <AssignmentTurnedInIcon />, path: "/java-assignment-solutions" },
                ].map((item) => (
                    <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
                        <ListItemButton
                            onClick={() => navigate(item.path)}
                            sx={{ borderRadius: "8px", "&:hover": { bgcolor: "rgba(229, 201, 95, 0.1)" } }}
                        >
                            <ListItemIcon sx={{ color: GOLD }}>{item.icon}</ListItemIcon>
                            <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: 600, fontSize: "0.9rem" }} />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
        </Box>
    );

    return (
        <Box sx={{ display: "flex", bgcolor: NAVY_DEEP, minHeight: "100vh" }}>
            {/* AppBar for Mobile Navigation */}
            <AppBar position="fixed" sx={{
                width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
                ml: { md: `${DRAWER_WIDTH}px` },
                bgcolor: "rgba(16, 7, 32, 0.8)",
                backdropFilter: "blur(10px)",
                boxShadow: "none",
                borderBottom: "1px solid rgba(255,255,255,0.05)"
            }}>
                <Toolbar>
                    <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, display: { md: "none" } }}>
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h6" fontWeight={800} noWrap component="div">
                        Assessment and Quiz Dashboard
                    </Typography>
                </Toolbar>
            </AppBar>

            {/* Sidebar Drawer */}
            <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    sx={{ display: { xs: "block", md: "none" }, "& .MuiDrawer-paper": { width: DRAWER_WIDTH, border: "none" } }}
                >
                    {drawerContent}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{ display: { xs: "none", md: "block" }, "& .MuiDrawer-paper": { width: DRAWER_WIDTH, border: "none" } }}
                    open
                >
                    {drawerContent}
                </Drawer>
            </Box>

            {/* Main Content Area */}
            <Box component="main" sx={{ flexGrow: 1, p: 3, width: { md: `calc(100% - ${DRAWER_WIDTH}px)` }, mt: 8 }}>
                <Container maxWidth="lg">

                    {/* 1. ANALYTICS STRIP */}
                    <Grid container spacing={3} sx={{ mb: 6 }}>
                        {stats.map((stat, i) => (
                            <Grid item xs={12} sm={4} key={i}>
                                <StatCard elevation={0}>
                                    <Avatar sx={{ bgcolor: "rgba(229, 201, 95, 0.1)", p: 1 }}>{stat.icon}</Avatar>
                                    <Box>
                                        <Typography variant="caption" sx={{ color: TEXT_MUTED, fontWeight: 700 }}>{stat.label}</Typography>
                                        <Typography variant="h5" sx={{ color: "white", fontWeight: 900 }}>{stat.count}</Typography>
                                    </Box>
                                </StatCard>
                            </Grid>
                        ))}
                    </Grid>

                    {/* 2. ASSIGNMENT GRID */}
                    <Typography variant="h5" sx={{ color: GOLD, fontWeight: 900, mb: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CodeIcon /> Language Assignments
                    </Typography>

                    <Grid container spacing={4} sx={{ mb: 8 }}>
                        {courses.map((course, i) => (
                            <Grid item xs={12} sm={6} md={6} key={i}>
                                <CourseCard elevation={0} onClick={() => navigate(course.path)}>
                                    <Box className="icon-bg" sx={{ position: 'absolute', right: -10, bottom: -10, opacity: 0.05, transition: '0.5s' }}>
                                        {course.icon}
                                    </Box>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                        <Box>
                                            <Typography variant="h6" fontWeight={900} sx={{ color: "white", mb: 1 }}>{course.name}</Typography>
                                            <Chip label="Master Assignment" size="small" sx={{ bgcolor: "rgba(255,255,255,0.05)", color: GOLD, border: `1px solid ${GOLD}`, fontWeight: 800, fontSize: '0.65rem' }} />
                                        </Box>
                                        <Box sx={{ color: course.color }}>{course.icon}</Box>
                                    </Stack>

                                    <Divider sx={{ my: 3, bgcolor: "rgba(255,255,255,0.05)" }} />

                                    <Stack direction="row" spacing={2}>
                                        <Button
                                            variant="text"
                                            size="small"
                                            onClick={(e) => { e.stopPropagation(); navigate(course.quiz); }}
                                            sx={{ color: GOLD, fontWeight: 700, fontSize: '0.75rem' }}
                                            startIcon={<QuizIcon fontSize="small" />}
                                        >
                                            {course.totalQuizzes} Quizzes Available
                                        </Button>
                                    </Stack>
                                </CourseCard>
                            </Grid>
                        ))}
                    </Grid>

                    {/* 3. QUICK LINKS & RESOURCES */}
                    <Typography variant="h5" sx={{ color: GOLD, fontWeight: 900, mb: 4 }}>
                        Platform Reference
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, bgcolor: "rgba(0, 237, 100, 0.05)", border: "1px solid rgba(0, 237, 100, 0.2)", borderRadius: 3 }}>
                                <Typography variant="subtitle1" sx={{ color: "#00ED64", fontWeight: 800, mb: 1 }}>Official Examination</Typography>
                                <Typography variant="body2" sx={{ color: TEXT_MUTED, mb: 2 }}>Ready to certify? Complete the proctored exam to earn your Finnish Engineering Standard badge.</Typography>
                                <MuiLink component={Link} to="/quizzes" sx={{ color: "#00ED64", fontWeight: 700, textDecoration: 'none' }}>Go to Quizzes →</MuiLink>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, bgcolor: "rgba(13, 183, 237, 0.05)", border: "1px solid rgba(13, 183, 237, 0.2)", borderRadius: 3 }}>
                                <Typography variant="subtitle1" sx={{ color: "#0DB7ED", fontWeight: 800, mb: 1 }}>Interview Strategy</Typography>
                                <Typography variant="body2" sx={{ color: TEXT_MUTED, mb: 2 }}>Access our high-frequency DSA questions curated specifically for FAANG and European tech hubs.</Typography>
                                <MuiLink component={Link} to="/project-list" sx={{ color: "#0DB7ED", fontWeight: 700, textDecoration: 'none' }}>View Algorithms →</MuiLink>
                            </Paper>
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 10, textAlign: 'center', opacity: 0.3 }}>
                        <Typography variant="caption">© 2026 PRODEV CONSULT · HELSINKI, FINLAND</Typography>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
}

// Internal MuiLink Helper to avoid conflict with React Router Link
const MuiLink = styled(Typography)(({ theme }) => ({
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' }
}));
